// Sheep Daily Reports' Home-dashboard reconciliation boundary.
//
// The list and detail surfaces own their own full-history reads. Home consumes
// only the recent fourteen-day window through DailysRecentContext, so a local
// mutation can reconcile that small projection without reaching back through
// App's global refresh closure.
import {addDays, toISO} from '../lib/dateUtils.js';

export const SHEEP_DAILYS_RECENT_WINDOW_DAYS = 14;

export type SheepDailyRecentRow = {
  id: string;
  date?: string | null;
  deleted_at?: string | null;
};

export function sheepDailysRecentCutoff(now: Date = new Date()): string {
  // Keep the exact calendar calculation boot uses for sheepDailysRecent.
  return toISO(addDays(now, -SHEEP_DAILYS_RECENT_WINDOW_DAYS));
}

function belongsInSheepDailysRecent(row: SheepDailyRecentRow, cutoff: string): boolean {
  return Boolean(row.id && row.date && row.date >= cutoff && !row.deleted_at);
}

/**
 * Replace one record in Home's bounded projection after a successful local
 * create or edit. Existing rows are normalized to the same window too, so a
 * mutation cannot leave an out-of-window or deleted row behind.
 */
export function reconcileSheepDailysRecent<Row extends SheepDailyRecentRow>(
  rows: readonly Row[],
  record: Row,
  now: Date = new Date(),
): Row[] {
  const cutoff = sheepDailysRecentCutoff(now);
  const retained = rows.filter((row) => row.id !== record.id && belongsInSheepDailysRecent(row, cutoff));
  if (!belongsInSheepDailysRecent(record, cutoff)) return retained;

  // App's boot read orders this projection by date descending. Modern engines
  // preserve relative ordering for equal dates, which also keeps boot rows
  // stable when a same-day record is reconciled.
  return [...retained, record].sort((a, b) => String(b.date).localeCompare(String(a.date)));
}

/** Remove a successful soft-delete from Home's bounded projection immediately. */
export function removeSheepDailyFromRecent<Row extends {id: string}>(rows: readonly Row[], id: string): Row[] {
  return rows.filter((row) => row.id !== id);
}
